import React from 'react';
import { KvTable, KvRow } from './KvTable';
import { ensureTrailingEmptyKvRow, makeKvRowId } from '../../utils/kvRows';

/** Rows → what goes on the wire: enabled rows with a name. */
export const headersToObject = (rows) => Object.fromEntries(
  (rows || []).filter((h) => h.enabled !== false && String(h.key || '').trim()).map((h) => [h.key.trim(), String(h.value ?? '')]),
);

/**
 * A header table — the HTTP builder's key/value table with its habits: a fresh empty row
 * appears as soon as the last one gets a name (no "Add" button), a checkbox switches a row
 * off without deleting it. Controlled: `rows` in, `onChange(rows)` out.
 *
 * @param {{ rows: object[], onChange: (rows: object[]) => void, idPrefix: string, disabled?: boolean }} props
 */
export default function HeaderKvEditor({ rows, onChange, idPrefix, disabled = false }) {
  const list = ensureTrailingEmptyKvRow(rows, idPrefix);
  const update = (idx, field, value) => {
    const next = list.map((h, i) => (i === idx ? { ...h, [field]: value } : h));
    if (next[next.length - 1].key !== '') next.push({ key: '', value: '', enabled: true, _id: makeKvRowId(idPrefix) });
    onChange(next);
  };
  const remove = (idx) => onChange(ensureTrailingEmptyKvRow(list.filter((_, i) => i !== idx), idPrefix));
  const field = (h, idx, name, placeholder, label) => (
    <input
      type="text"
      value={h[name] ?? ''}
      onChange={(e) => update(idx, name, e.target.value)}
      placeholder={placeholder}
      aria-label={label}
      className="request-builder-kv-input w-full rounded-md focus:outline-none"
      disabled={disabled}
      spellCheck={false}
    />
  );

  return (
    <KvTable>
      {list.map((h, idx) => (
        <KvRow
          key={h._id ?? idx}
          enabled={h.enabled !== false}
          onToggleEnabled={(v) => update(idx, 'enabled', v)}
          checkboxTabIndex={-1}
          showRemove={!(idx === list.length - 1 && h.key === '')}
          onRemove={() => remove(idx)}
          removeDisabled={disabled}
          renderKey={field(h, idx, 'key', 'Key', 'Header name')}
          renderValue={field(h, idx, 'value', 'Value', 'Header value')}
        />
      ))}
    </KvTable>
  );
}
